/**
 * How many decimal places a currency's minor unit has, per ISO 4217. Only the
 * exceptions are listed; everything absent uses the 2-decimal default.
 *
 * Mirrors CURRENCY_MINOR_UNIT_EXCEPTIONS in
 * `backend/shared/util/src/countries` -- amounts cross the API boundary as
 * minor units, so both sides have to agree on what a minor unit is. That is
 * not a style preference: this table had drifted, missing GNF and XAF (both
 * zero-decimal) and JOD and OMR (both three-decimal), so a Guinean price the
 * backend stored as 500,000 GNF came back rendered "GNF 5,000.00" and a
 * Jordanian one was shown ten times over. Add a currency to both or neither.
 *
 * Getting this wrong is not cosmetic. Every amount used to be built with
 * Dinero's implicit 2-decimal precision, so a stored ¥5,000 rendered as
 * "¥50.00" and compared against price-filter bounds as 50 -- a factor of 100
 * out, silently, in whichever direction the currency deviates.
 */
const MINOR_UNIT_EXCEPTIONS: Record<string, number> = {
    // Zero-decimal currencies: the minor unit IS the major unit.
    CLP: 0,
    DJF: 0,
    GNF: 0,
    ISK: 0,
    JPY: 0,
    KRW: 0,
    PYG: 0,
    UGX: 0,
    VND: 0,
    XAF: 0,
    XOF: 0,
    // Three-decimal currencies.
    BHD: 3,
    IQD: 3,
    JOD: 3,
    KWD: 3,
    LYD: 3,
    OMR: 3,
    TND: 3,
};

export const DEFAULT_MINOR_UNIT_DIGITS = 2;

/**
 * Decimal places in `currencyCode`'s minor unit. Unknown codes get the
 * 2-decimal default, which is right for the large majority of ISO 4217.
 */
export const getMinorUnitDigits = (currencyCode?: string | null): number => {
    if (!currencyCode) return DEFAULT_MINOR_UNIT_DIGITS;
    return (
        MINOR_UNIT_EXCEPTIONS[currencyCode.toUpperCase()] ??
        DEFAULT_MINOR_UNIT_DIGITS
    );
};

/** The factor between a currency's minor and major units (100 for EUR, 1 for JPY). */
export const minorUnitFactor = (currencyCode?: string | null): number =>
    10 ** getMinorUnitDigits(currencyCode);

/**
 * Dinero's `toFormat` mask for a currency, so a zero-decimal currency is not
 * rendered with a meaningless ".00" and a three-decimal one is not truncated.
 */
export const currencyFormatMask = (currencyCode?: string | null): string => {
    const digits = getMinorUnitDigits(currencyCode);
    if (digits === 0) return "$0,0";
    return `$0,0.${"0".repeat(digits)}`;
};
